import { useState, useEffect, useMemo } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StepProgress } from "../components/StepProgress";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ArrowRight,
  ArrowLeft,
  Cpu,
  Info,
  Building2,
  Sparkles,
  Leaf,
  RefreshCw,
  Cloud,
  Layers,
  Check,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createAssessment, getAssessment, getAssessmentById, updateAssessment, trackEvent, getQuestionsGrouped, questionAppliesToScenario } from "../lib/api";
import LoadDemoProfileButton, { useDemoMode } from "../components/LoadDemoProfileButton";
import DemoProfileBanner from "../components/DemoProfileBanner";

// ---------------------------------------------------------------------------
// Assessment Type is a WORKFLOW SELECTOR — not a question. It controls the
// SmartEvaluate path (greenfield / brownfield / cloud_migration) and is fully
// hardcoded here. It is intentionally NOT read from the Admin question bank.
// Only the client-detail fields below remain admin-driven.
// ---------------------------------------------------------------------------
const ASSESSMENT_TYPES = [
  {
    value: "greenfield",
    title: "Greenfield",
    badge: "New Implementation",
    description: "New SAP warehouse implementation.",
    Icon: Leaf,
  },
  {
    value: "brownfield",
    title: "Brownfield",
    badge: "Upgrade / Transformation",
    description: "Existing SAP warehouse modernization or upgrade.",
    Icon: RefreshCw,
  },
  {
    value: "cloud_migration",
    title: "Cloud Migration",
    badge: "Cloud Transformation",
    description: "Assess readiness for SAP warehouse cloud migration.",
    Icon: Cloud,
  },
  {
    value: "mixed_landscape",
    title: "Mixed Landscape",
    badge: "Multiple Paths",
    description:
      "Different warehouse groups may follow different transformation paths.",
    Icon: Layers,
  },
];

// Feature flag — only types listed here are shown. Add "cloud_migration" to
// enable the Cloud Migration card when ready.
const ENABLED_ASSESSMENT_TYPES = ["greenfield", "brownfield", "cloud_migration", "mixed_landscape"];
const VISIBLE_ASSESSMENT_TYPES = ASSESSMENT_TYPES.filter((t) =>
  ENABLED_ASSESSMENT_TYPES.includes(t.value)
);

// Assessment Context questions (admin-managed, in module1/client_details under
// the "Assessment Context" sub-section) are shown conditionally per selected
// assessment type. Keyed by question field_key. Answers are persisted under
// module_data.module2 so downstream engines read the same values as before.
const CONTEXT_SUBSECTION = "Assessment Context";
// Step 1 Assessment Context is fully Admin/question-bank driven. Visibility is
// controlled by each question's active flag + evaluation_types (no hardcoded
// key list here). Current SAP Solution / Preserve Architecture are retired from
// Step 1 (active=false) and captured per Warehouse Group in Step 2 instead.

const INPUT_CLS =
  "rounded-sm h-11 text-sm border-[#D1D5DB] text-[#1F2937] placeholder:text-[#9CA3AF] focus:border-[#025979] focus:ring-[#025979] transition-colors";

// HTML input type by field_key — pure validation/presentation concern.
const inputTypeFor = (fk) => (fk === "email" ? "email" : fk === "phone" ? "tel" : "text");

export default function Module1Page() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  // Contributors share the owner's assessment; Step 1 is read-only for them.
  const [readOnly, setReadOnly] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState({ scenario_type: "" });
  const { enabled: demoEnabled, profiles: demoProfiles } = useDemoMode();

  // Admin-driven client-detail fields. The legacy scenario_type question (if it
  // still lingers in the bank) is explicitly excluded — assessment type is now
  // a hardcoded workflow selector, never a question.
  const inputQuestions = useMemo(
    () => questions.filter((q) => q.field_key !== "scenario_type"),
    [questions]
  );

  // Plain client-detail text fields (everything NOT in the Assessment Context
  // sub-section).
  const clientFields = useMemo(
    () => inputQuestions.filter((q) => (q.subsection_heading || "") !== CONTEXT_SUBSECTION),
    [inputQuestions]
  );

  // Assessment Context questions — rendered conditionally per assessment type.
  const contextQuestions = useMemo(
    () =>
      inputQuestions
        .filter((q) => (q.subsection_heading || "") === CONTEXT_SUBSECTION)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0)),
    [inputQuestions]
  );

  const visibleContextQuestions = useMemo(() => {
    if (!form.scenario_type) return [];
    // Fully Admin-driven: show a context question when its evaluation_types
    // include the selected assessment type (mixed_landscape handled in the
    // helper). Retired questions (active=false in the bank) never reach here.
    return contextQuestions.filter((q) => questionAppliesToScenario(q, form.scenario_type));
  }, [contextQuestions, form.scenario_type]);

  // Load Client Details questions from Admin + rehydrate any in-progress draft.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getQuestionsGrouped("module1");
        const qs = Object.values(data.sections || {})
          .flat()
          .filter((q) => q.field_key !== "scenario_type");
        if (cancelled) return;
        setQuestions(qs);

        // Seed the form shape from the question bank (apply admin defaults).
        const initial = { scenario_type: "" };
        qs.forEach((q) => {
          if (!q.field_key) return;
          // Assessment Context (single-select scoring) questions must start
          // blank on a fresh assessment — no default preselection. Their value
          // is only set by explicit user choice, resume, or demo profile.
          if ((q.subsection_heading || "") === CONTEXT_SUBSECTION) {
            initial[q.field_key] = "";
          } else {
            initial[q.field_key] = q.default_value || "";
          }
        });

        // Resume support: only rehydrate when BOTH email AND assessment_id are
        // present, and the draft is not already submitted/completed.
        const email = localStorage.getItem("warehousex_email");
        const draftId = localStorage.getItem("warehousex_assessment_id");
        if (email && draftId) {
          try {
            let a = await getAssessment(email);
            // Contributor: the owner-scoped lookup misses their email — load the
            // SAME shared assessment by id so Step 1 shows the owner's existing
            // data (read-only). Owner flow resolves above, so no id fetch there.
            if (!a) a = await getAssessmentById(draftId);
            if (a && a.status !== "submitted" && a.status !== "completed") {
              const ownerEmail = (a.email || "").trim().toLowerCase();
              if (!cancelled) {
                setReadOnly(!!ownerEmail && (email || "").trim().toLowerCase() !== ownerEmail);
              }
              qs.forEach((q) => {
                const fk = q.field_key;
                if (fk && a[fk] !== undefined && a[fk] !== null) initial[fk] = a[fk];
              });
              if (a.email) initial.email = a.email;
              if (a.scenario_type) initial.scenario_type = a.scenario_type;
              // Assessment Context answers live in module_data.module2.
              const savedM2 = (a.module_data && a.module_data.module2) || {};
              qs.forEach((q) => {
                const fk = q.field_key;
                if (
                  fk &&
                  (q.subsection_heading || "") === CONTEXT_SUBSECTION &&
                  savedM2[fk] !== undefined &&
                  savedM2[fk] !== null
                ) {
                  initial[fk] = savedM2[fk];
                }
              });
            }
          } catch { /* fresh form */ }
        }
        if (!cancelled) setForm(initial);
      } catch (e) {
        console.error("Failed to load questions:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const isRequired = (q) => q.is_required !== false;

  const validate = () => {
    const errs = {};
    clientFields.forEach((q) => {
      const fk = q.field_key;
      if (!fk) return;
      const val = form[fk];
      const empty = val === undefined || val === null || String(val).trim() === "";
      if (isRequired(q) && empty) {
        errs[fk] = `${q.question_text} is required`;
        return;
      }
      if (empty) return;
      // Format validation by field_key — kept in code intentionally.
      if (fk === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        errs[fk] = "Enter a valid email address";
      } else if (fk === "phone" && !/^\+?[\d\s\-()]{7,20}$/.test(val)) {
        errs[fk] = "Enter a valid phone number";
      }
    });
    // Assessment type is always required (workflow selector).
    if (!form.scenario_type) errs.scenario_type = "Please select an assessment type";
    return errs;
  };

  const handleContinue = async () => {
    // Contributors can't edit/create Step 1 — just move forward on the shared
    // assessment (id + their email in localStorage stay untouched).
    if (readOnly) { navigate("/evaluate/module-2"); return; }
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      const assessment = await createAssessment(form);
      // Persist Assessment Context answers (only those shown for the selected
      // type) into module_data.module2 so downstream engines read the same
      // values they always have.
      const ctx = {};
      visibleContextQuestions.forEach((q) => {
        const v = form[q.field_key];
        const emptyArr = Array.isArray(v) && v.length === 0;
        if (v !== undefined && v !== null && v !== "" && !emptyArr) ctx[q.field_key] = v;
      });
      if (Object.keys(ctx).length > 0) {
        try {
          await updateAssessment(assessment.id, { module_data: { module2: ctx } });
        } catch (e) {
          console.error("Failed to save assessment context:", e);
        }
      }
      localStorage.setItem("warehousex_email", form.email);
      localStorage.setItem("warehousex_assessment_id", assessment.id);
      trackEvent("module1_completed", form.email, {
        scenario: form.scenario_type,
        assessment_id: assessment.id,
      });
      navigate("/evaluate/module-2");
    } catch (e) {
      console.error("Failed to save assessment:", e);
      setErrors({ _general: "Failed to save. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-sm text-[#6B7280]">Loading questions...</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <Header />

      <StepProgress currentStep={1} />

      <main className="flex-1 pt-8 sm:pt-10 pb-12">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Demo profile applied banner — shared across Module 1/2/3. */}
          <DemoProfileBanner stepNumber={1} />

          {/* Page header */}
          <div className="mb-7 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 bg-[#E0F2FE] border border-[#025979]/15 rounded-sm px-3 py-1.5 mb-2">
                <Cpu className="w-3.5 h-3.5 text-[#025979]" strokeWidth={1.5} />
                <span className="text-xs font-medium text-[#025979]">Step 1 — Company Profile</span>
              </div>
              <p
                data-testid="module1-step-indicator"
                className="text-[11px] text-[#9CA3AF] mb-4 tracking-wide"
              >
                Step 1 of 3 • Takes ~2 minutes
              </p>
              <h1
                data-testid="module1-title"
                className="text-xl sm:text-2xl font-bold tracking-tight text-[#1F2937]"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                Begin Your Warehouse Evaluation
              </h1>
              <p
                data-testid="module1-ai-line"
                className="mt-2 text-[13px] text-[#6B7280] leading-relaxed max-w-2xl"
              >
                We&apos;ll guide you step-by-step using SAP best practices and AI-assisted insights.
              </p>
              <p data-testid="module1-subtitle" className="mt-1 text-[13px] text-[#4B5563] leading-relaxed max-w-2xl">
                Tell us about your organization to personalize your evaluation.
              </p>
            </div>

            {demoEnabled && demoProfiles.length > 0 && (
              <div className="shrink-0 pt-2">
                <LoadDemoProfileButton
                  profiles={demoProfiles}
                  preselectProfileId={
                    typeof window !== "undefined"
                      ? new URLSearchParams(window.location.search).get("demo_profile")
                      : null
                  }
                  existingAssessmentId={
                    typeof window !== "undefined"
                      ? localStorage.getItem("warehousex_assessment_id")
                      : null
                  }
                  existingEmail={
                    typeof window !== "undefined"
                      ? localStorage.getItem("warehousex_email")
                      : null
                  }
                  onApplied={({ profile }) => {
                    const name = encodeURIComponent(profile?.profile_name || "profile");
                    window.location.href = `/evaluate/module-1?demo=${name}`;
                  }}
                  variant="compact"
                />
              </div>
            )}
          </div>

          {/* Main form card */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm">

            {readOnly && (
              <div data-testid="module1-readonly-banner" className="px-6 sm:px-8 py-2.5 text-xs font-medium text-[#025979] bg-[#E0F2FE]/50 border-b border-[#025979]/10">
                Contributor view — Step 1 is read-only. Only the evaluation owner can edit this setup.
              </div>
            )}
            <fieldset disabled={readOnly} className={`min-w-0 border-0 p-0 m-0${readOnly ? " pointer-events-none opacity-95" : ""}`}>

            {/* Client Details — rendered entirely from the Admin question bank */}
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-5">
                <Building2 className="w-4 h-4 text-[#025979]" strokeWidth={1.5} />
                <h2 className="text-sm font-semibold text-[#1F2937] uppercase tracking-wider" style={{ fontFamily: "Chivo, sans-serif" }}>
                  Tell us about your organization
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-5" data-testid="module1-fields">
                {clientFields.map((q) => {
                  const fk = q.field_key;
                  const fullWidth = fk === "company_name";
                  return (
                    <div key={q.id} className={fullWidth ? "sm:col-span-2" : ""}>
                      <Label htmlFor={fk} className="text-xs font-medium text-[#1F2937] mb-1.5 block">
                        {q.question_text}{" "}
                        {isRequired(q)
                          ? <span className="text-red-500">*</span>
                          : <span className="text-[#6B7280] font-normal">(optional)</span>}
                      </Label>
                      <Input
                        id={fk}
                        data-testid={`field-${fk}`}
                        type={inputTypeFor(fk)}
                        placeholder={q.placeholder || ""}
                        value={form[fk] || ""}
                        onChange={(e) => updateField(fk, e.target.value)}
                        className={`${INPUT_CLS} ${errors[fk] ? "border-red-400" : ""}`}
                      />
                      {q.help_text && (
                        <p data-testid={`helper-${fk}`} className="text-[11px] text-[#9CA3AF] mt-1.5">
                          {q.help_text}
                        </p>
                      )}
                      {errors[fk] && <p data-testid={`error-${fk}`} className="text-xs text-red-500 mt-1.5">{errors[fk]}</p>}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-[#E5E7EB]"></div>

            {/* Assessment Type — hardcoded workflow selector (feature-flagged) */}
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-[#6E5BCE]" strokeWidth={1.5} />
                <h2 className="text-sm font-semibold text-[#1F2937] uppercase tracking-wider" style={{ fontFamily: "Chivo, sans-serif" }}>
                  Choose Assessment Type
                </h2>
              </div>
              <p
                data-testid="scenario-instruction"
                className="text-[13px] text-[#374151] font-medium mb-5"
              >
                Select the assessment path that best matches your current warehouse initiative.
              </p>

              <div
                data-testid="field-scenario_type"
                role="radiogroup"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
              >
                {VISIBLE_ASSESSMENT_TYPES.map((t) => {
                  const selected = form.scenario_type === t.value;
                  const Icon = t.Icon;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      data-testid={`scenario-option-${t.value}`}
                      data-state={selected ? "selected" : "unselected"}
                      onClick={() => updateField("scenario_type", t.value)}
                      className={[
                        "group relative text-left p-5 sm:p-6 rounded-sm border-2 transition-all duration-200",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#025979]/30",
                        selected
                          ? "border-[#025979] bg-[#E0F2FE]/60 shadow-sm"
                          : "border-[#E5E7EB] bg-white hover:border-[#03738C] hover:shadow-md hover:-translate-y-0.5",
                      ].join(" ")}
                    >
                      {selected && (
                        <span
                          aria-hidden
                          className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#025979] flex items-center justify-center"
                        >
                          <Check className="w-3 h-3 text-white" strokeWidth={3} />
                        </span>
                      )}
                      <div
                        className={[
                          "w-10 h-10 rounded-sm flex items-center justify-center mb-3 transition-colors",
                          selected ? "bg-[#025979] text-white" : "bg-[#E0F2FE] text-[#025979] group-hover:bg-[#03738C] group-hover:text-white",
                        ].join(" ")}
                      >
                        <Icon className="w-5 h-5" strokeWidth={1.8} />
                      </div>
                      {/* Title — fixed height for alignment */}
                      <div className="min-h-[20px] flex items-center text-sm font-semibold text-[#1F2937]" style={{ fontFamily: "Chivo, sans-serif" }}>
                        {t.title}
                      </div>
                      {/* Badge — own line, fixed height */}
                      <div className="min-h-[24px] flex items-center mt-1.5">
                        <span
                          data-testid={`scenario-tag-${t.value}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#E0F2FE] text-[#025979] border border-[#025979]/15 text-[10px] font-medium"
                        >
                          {t.badge}
                        </span>
                      </div>
                      {/* Description — reserves space for up to two lines */}
                      <p className="min-h-[40px] text-[13px] text-[#4B5563] mt-2 leading-relaxed">
                        {t.description}
                      </p>
                    </button>
                  );
                })}
              </div>
              {errors.scenario_type && (
                <p data-testid="error-scenario_type" className="text-xs text-red-500 mt-3">
                  {errors.scenario_type}
                </p>
              )}

              <p className="mt-4 text-xs text-[#6B7280]">
                SmartEvaluate will tailor questions and recommendations based on your selection.
              </p>
            </div>

            {/* Assessment Context — admin-managed questions shown conditionally
                per selected assessment type. Answers persist to module_data.module2. */}
            {visibleContextQuestions.length > 0 && (
              <>
                <div className="border-t border-[#E5E7EB]"></div>
                <div className="p-6 sm:p-8" data-testid="assessment-context-section">
                  <div className="flex items-center gap-2 mb-5">
                    <Building2 className="w-4 h-4 text-[#025979]" strokeWidth={1.5} />
                    <h2 className="text-sm font-semibold text-[#1F2937] uppercase tracking-wider" style={{ fontFamily: "Chivo, sans-serif" }}>
                      Assessment Context
                    </h2>
                  </div>
                  <div className="space-y-6">
                    {visibleContextQuestions.map((q) => {
                      const fk = q.field_key;
                      const type = q.answer_type || "single_select";
                      const req = q.is_required !== false;
                      return (
                        <div key={q.id} data-testid={`context-field-${fk}`}>
                          <Label className="text-xs font-medium text-[#1F2937] mb-1.5 block">
                            {q.question_text} {req && <span className="text-red-500">*</span>}
                          </Label>
                          {q.help_text && (
                            <p data-testid={`helper-${fk}`} className="text-[11px] text-[#9CA3AF] -mt-0.5 mb-2">{q.help_text}</p>
                          )}
                          {type === "single_select" ? (
                            <div data-testid={`field-${fk}`} role="radiogroup" className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                              {(q.answer_options || []).map((o) => {
                                const selected = form[fk] === o.value;
                                return (
                                  <button
                                    key={o.value}
                                    type="button"
                                    role="radio"
                                    aria-checked={selected}
                                    data-testid={`field-${fk}-${o.value}`}
                                    data-state={selected ? "selected" : "unselected"}
                                    onClick={() => updateField(fk, o.value)}
                                    className={`text-left p-3 rounded-sm border cursor-pointer transition-colors ${
                                      selected
                                        ? "border-[#025979] bg-[#E0F2FE]/50"
                                        : "border-[#E5E7EB] hover:border-[#D1D5DB] hover:bg-gray-50"
                                    }`}
                                  >
                                    <span className="text-sm font-medium text-[#111827] block">{o.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : type === "multi_select" ? (
                            <>
                              <div data-testid={`field-${fk}`} role="group" className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                {(q.answer_options || []).map((o) => {
                                  const arr = Array.isArray(form[fk]) ? form[fk] : [];
                                  const selected = arr.includes(o.value);
                                  const max = q.max_selections || 0;
                                  const atMax = max > 0 && arr.length >= max && !selected;
                                  return (
                                    <button
                                      key={o.value}
                                      type="button"
                                      role="checkbox"
                                      aria-checked={selected}
                                      disabled={atMax}
                                      data-testid={`field-${fk}-${o.value}`}
                                      data-state={selected ? "selected" : "unselected"}
                                      onClick={() => {
                                        const next = selected
                                          ? arr.filter((x) => x !== o.value)
                                          : (max > 0 && arr.length >= max ? arr : [...arr, o.value]);
                                        updateField(fk, next);
                                      }}
                                      className={`text-left p-3 rounded-sm border transition-colors ${
                                        selected
                                          ? "border-[#025979] bg-[#E0F2FE]/50 cursor-pointer"
                                          : atMax
                                            ? "border-[#E5E7EB] bg-gray-50 text-[#9CA3AF] cursor-not-allowed"
                                            : "border-[#E5E7EB] hover:border-[#D1D5DB] hover:bg-gray-50 cursor-pointer"
                                      }`}
                                    >
                                      <span className="text-sm font-medium text-[#111827] block">{o.label}</span>
                                    </button>
                                  );
                                })}
                              </div>
                              {q.max_selections ? (
                                <p data-testid={`max-hint-${fk}`} className="text-[11px] text-[#6B7280] mt-1.5">
                                  Select up to {q.max_selections}.
                                </p>
                              ) : null}
                            </>
                          ) : type === "dropdown" ? (
                            <Select value={form[fk] || ""} onValueChange={(v) => updateField(fk, v)}>
                              <SelectTrigger data-testid={`field-${fk}`} className="rounded-sm h-11 text-sm border-[#D1D5DB] focus:border-[#025979] focus:ring-[#025979]">
                                <SelectValue placeholder={q.placeholder || "Select an option"} />
                              </SelectTrigger>
                              <SelectContent>
                                {(q.answer_options || []).map((o) => (
                                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              data-testid={`field-${fk}`}
                              type={type === "number" || type === "optional_number" ? "number" : "text"}
                              value={form[fk] || ""}
                              placeholder={q.placeholder || ""}
                              onChange={(e) => updateField(fk, e.target.value)}
                              className={INPUT_CLS}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <div className="border-t border-[#E5E7EB]"></div>

            {/* AI Guidance Panel */}
            <div className="p-6 sm:p-8">
              <div data-testid="module1-ai-guidance" className="flex items-start gap-2.5 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-4 py-3">
                <Info className="w-4 h-4 text-[#025979] mt-0.5 shrink-0" strokeWidth={1.5} />
                <span className="text-sm text-[#4B5563] leading-relaxed">
                  Based on your selected scenario, SmartEvaluate will tailor the next steps to understand your warehouse landscape and recommend the right SAP strategy.
                </span>
              </div>
            </div>

            </fieldset>

            <div className="border-t border-[#E5E7EB]"></div>

            {/* Footer actions */}
            <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-3">
              <Button
                data-testid="module1-back-btn"
                variant="outline"
                onClick={() => navigate("/evaluate")}
                className="rounded-sm h-11 px-6 text-sm font-medium border-[#025979] text-[#025979] hover:bg-[#E0F2FE] hover:text-[#025979] hover:border-[#025979] transition-colors"
              >
                <ArrowLeft className="mr-2 w-4 h-4" />
                Back
              </Button>

              {errors._general && <p className="text-xs text-red-500">{errors._general}</p>}

              <Button
                data-testid="module1-continue-btn"
                onClick={handleContinue}
                disabled={submitting}
                className="bg-[#025979] hover:bg-[#03738C] text-white h-11 px-8 text-sm font-semibold rounded-sm transition-colors shadow-sm hover:shadow disabled:opacity-50"
              >
                {submitting ? "Saving…" : "Continue to Warehouse Landscape"}
                {!submitting && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
